import { useCallback, useEffect, useMemo } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import {
  LOCAL_STORAGE_TENANT_ID_KEY,
  SESSION_STORAGE_TENANT_ID_KEY,
} from '../../constants/storage-key';
import { resolveTenantIdAlias } from '../../utils/resolve-tenant-id-alias';

const LEGACY_SESSION_STORAGE_TENANT_ID_KEY = 'tenantId';

const readStorageTenantId = (storage: Storage, key: string) => {
  const storedTenantId = storage.getItem(key);

  if (!storedTenantId) {
    return '';
  }

  try {
    const parsedTenantId: unknown = JSON.parse(storedTenantId);

    return typeof parsedTenantId === 'string' ? parsedTenantId : '';
  } catch {
    return storedTenantId;
  }
};

const readLocalStorageTenantId = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return readStorageTenantId(window.localStorage, LOCAL_STORAGE_TENANT_ID_KEY);
};

const readSessionStorageTenantId = (key: string) => {
  if (typeof window === 'undefined') {
    return '';
  }

  return readStorageTenantId(window.sessionStorage, key);
};

/**
 * Manages the tenant ID using session storage with a localStorage fallback.
 *
 * This hook provides functions to access and update the tenant ID,
 * which is stored in session storage using the `useSessionStorage` hook from
 * `usehooks-ts`. When a fresh tab has no session-scoped tenant yet, the hook
 * falls back to the last selected tenant from localStorage so tenantless entry
 * URLs can still recover a tenant context.
 *
 * Tenant ID aliases (e.g. legacy 'b2b' stored from before the rename) are
 * resolved to their canonical form on read, and the canonical value is
 * persisted back to storage so the alias is only ever encountered once.
 *
 * @returns {Object} An object containing the following:
 * - `tenantId`: The current canonical tenant ID.
 * - `setTenantId`: A function to update the tenant ID.
 */
export const useTenantId = () => {
  const initialTenantIdValue = '';
  const [storedTenantId, setTenantId] = useSessionStorage<string>(
    SESSION_STORAGE_TENANT_ID_KEY,
    initialTenantIdValue,
  );
  const fallbackTenantId = useMemo(readLocalStorageTenantId, []);
  const legacySessionTenantId = useMemo(() => {
    if (typeof window === 'undefined') {
      return '';
    }

    if (window.sessionStorage.getItem(SESSION_STORAGE_TENANT_ID_KEY) !== null) {
      return '';
    }

    return readSessionStorageTenantId(LEGACY_SESSION_STORAGE_TENANT_ID_KEY);
  }, []);
  const hasStoredSessionTenantId =
    typeof window !== 'undefined'
      ? window.sessionStorage.getItem(SESSION_STORAGE_TENANT_ID_KEY) !== null ||
        window.sessionStorage.getItem(LEGACY_SESSION_STORAGE_TENANT_ID_KEY) !==
          null
      : storedTenantId !== initialTenantIdValue;
  const sessionTenantId = storedTenantId || legacySessionTenantId;

  const tenantId = resolveTenantIdAlias(
    hasStoredSessionTenantId ? sessionTenantId : fallbackTenantId,
  );

  const setPersistedTenantId = useCallback(
    (nextTenantId: string) => {
      const canonicalTenantId = resolveTenantIdAlias(nextTenantId);

      setTenantId(canonicalTenantId);

      if (typeof window === 'undefined') {
        return;
      }

      if (canonicalTenantId) {
        window.localStorage.setItem(
          LOCAL_STORAGE_TENANT_ID_KEY,
          JSON.stringify(canonicalTenantId),
        );
      } else {
        window.localStorage.removeItem(LOCAL_STORAGE_TENANT_ID_KEY);
      }
    },
    [setTenantId],
  );

  useEffect(() => {
    if (tenantId !== storedTenantId || legacySessionTenantId) {
      // Update the tab-scoped tenant directly here; the localStorage mirror is
      // reconciled in the block below so we do not duplicate that write path.
      setTenantId(tenantId);
    }

    if (legacySessionTenantId) {
      window.sessionStorage.removeItem(LEGACY_SESSION_STORAGE_TENANT_ID_KEY);
    }

    const localStorageTenantId = readLocalStorageTenantId();
    if (tenantId && localStorageTenantId !== tenantId) {
      window.localStorage.setItem(
        LOCAL_STORAGE_TENANT_ID_KEY,
        JSON.stringify(tenantId),
      );
    }
  }, [legacySessionTenantId, tenantId, storedTenantId, setTenantId]);

  return { tenantId, setTenantId: setPersistedTenantId };
};
